import { budgetHealth, type BudgetTotals } from '@budgetmonkey/shared'
import { Money } from '@/components/ui'

/**
 * The four figures from §7.4, shared between the code detail view and the
 * dashboard card so both always agree.
 *
 * "Reconciled" is deliberately not a label here — it names two different
 * events weeks apart (packet submitted vs. CSIU confirmed), which is exactly
 * the ambiguity §5.3 had to resolve. See CLAUDE.md, "Balance vocabulary".
 */
export function BalanceStrip({
  totals,
  alertThresholdPercent,
  size = 'large',
}: {
  totals: BudgetTotals
  alertThresholdPercent: number
  size?: 'large' | 'compact'
}) {
  const health = budgetHealth(totals, alertThresholdPercent)
  const valueClass = size === 'large' ? 'text-2xl' : 'text-lg'
  // The hints explain vocabulary once, on the detail screen. Repeating them on
  // eleven dashboard cards is noise.
  const hint = (text: string) => (size === 'large' ? text : undefined)

  return (
    <div className="space-y-4">
      {/* Two columns on a phone rather than four stacked figures — §7.4 is a
          check you make one-handed before buying something. */}
      <dl
        className={`grid grid-cols-2 gap-x-6 gap-y-4 ${
          size === 'large' ? 'sm:grid-cols-4' : ''
        }`}
      >
        <Figure label="Beginning" size={valueClass}>
          <Money value={totals.beginning} />
        </Figure>

        <Figure
          label="Spent"
          size={valueClass}
          hint={hint('Every transaction captured')}
        >
          <Money value={totals.spent} />
        </Figure>

        <Figure
          label="Remaining"
          size={valueClass}
          tone={
            health === 'over' ? 'danger' : health === 'approaching' ? 'warn' : 'normal'
          }
        >
          <Money value={totals.remaining} />
        </Figure>

        <Figure
          label="Confirmed"
          size={valueClass}
          hint={hint('Packet submitted or CSIU matched')}
        >
          <Money value={totals.confirmed} />
        </Figure>
      </dl>

      <SpendBar totals={totals} health={health} />

      {/* On dashboard cards the coloured bar and Remaining figure already
          carry this, and the dashboard's own "Needs attention" list says it
          in words. Repeating it eleven times would be noise. */}
      {size === 'large' && (
        <>
          {health === 'over' && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-800">
              Overspent by <Money value={Math.abs(totals.remaining)} />.
            </p>
          )}
          {health === 'approaching' && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
              {Math.round(totals.spentFraction * 100)}% spent — past the{' '}
              {alertThresholdPercent}% alert threshold.
            </p>
          )}

          {totals.spent !== totals.confirmed && totals.spent !== 0 && (
            <p className="text-sm text-slate-600">
              <Money value={totals.spent - totals.confirmed} /> spent but not yet
              confirmed — awaiting a submitted packet or a CSIU import.
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Figure({
  label,
  hint,
  size,
  tone = 'normal',
  children,
}: {
  label: string
  hint?: string
  size: string
  tone?: 'normal' | 'warn' | 'danger'
  children: React.ReactNode
}) {
  const toneClass =
    tone === 'danger' ? 'text-red-700' : tone === 'warn' ? 'text-amber-800' : 'text-slate-900'
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className={`${size} font-semibold ${toneClass}`}>{children}</dd>
      {hint !== undefined && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  )
}

/**
 * Spent-vs-remaining bar. The confirmed portion is solid and the in-flight
 * portion hatched, so the gap between "gone" and "acknowledged by someone
 * else" is visible at a glance (§7.4 step 2).
 */
function SpendBar({
  totals,
  health,
}: {
  totals: BudgetTotals
  health: 'ok' | 'approaching' | 'over'
}) {
  if (totals.beginning <= 0) return null

  const pct = (value: number) =>
    `${Math.min(100, Math.max(0, (value / totals.beginning) * 100))}%`

  const solid =
    health === 'over' ? 'bg-red-600' : health === 'approaching' ? 'bg-amber-500' : 'bg-slate-800'
  const light =
    health === 'over' ? 'bg-red-300' : health === 'approaching' ? 'bg-amber-300' : 'bg-slate-400'

  return (
    <div
      className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200"
      role="img"
      aria-label={`${Math.round(totals.spentFraction * 100)} percent of the beginning balance spent`}
    >
      <div className="flex h-full">
        <div className={solid} style={{ width: pct(totals.confirmed) }} />
        <div
          className={light}
          style={{ width: pct(Math.max(0, totals.spent - totals.confirmed)) }}
        />
      </div>
    </div>
  )
}
